import { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Person,
  Email,
  Phone,
  Badge,
} from '@mui/icons-material';
import { updateUserDocument } from '@services/userService';
import { notifyVolunteerApproval } from '@services/notificationService';

const PendingVolunteers = ({ volunteers, onUpdate }) => {
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleOpenDialog = (volunteer, action) => {
    setSelectedVolunteer(volunteer);
    setActionType(action);
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedVolunteer(null);
    setActionType(null);
  };

  const handleConfirmAction = async () => {
    if (!selectedVolunteer) return;

    try {
      setLoading(true);
      setError('');

      const newStatus = actionType === 'approve' ? 'active' : 'rejected';

      await updateUserDocument(selectedVolunteer.uid, {
        status: newStatus,
      });

      // Send notification to volunteer
      try {
        await notifyVolunteerApproval(
          selectedVolunteer.uid,
          actionType === 'approve',
          selectedVolunteer.organizationName
        );
      } catch (notifError) {
        console.error('Error sending notification to volunteer:', notifError);
        // Don't fail the approval if notification fails
      }

      // Call parent callback to refresh the list
      if (onUpdate) {
        await onUpdate();
      }

      handleCloseDialog();
    } catch (err) {
      console.error('Error updating volunteer status:', err);
      setError('Failed to update volunteer status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!volunteers || volunteers.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
        <Person sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No pending volunteers
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          All volunteers have been approved!
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={2} sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: 'warning.lighter', borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge color="warning" />
            <Typography variant="h6" fontWeight="bold">
              Pending Volunteer Approvals
            </Typography>
            <Chip
              label={volunteers.length}
              size="small"
              color="warning"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review and approve volunteers who have requested to join your organization
          </Typography>
        </Box>

        <List sx={{ p: 0 }}>
          {volunteers.map((volunteer, index) => (
            <Box key={volunteer.uid}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  py: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <Person />
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="medium">
                      {volunteer.displayName}
                    </Typography>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Email fontSize="small" sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {volunteer.email}
                        </Typography>
                      </Box>
                      {volunteer.phoneNumber && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Phone fontSize="small" sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {volunteer.phoneNumber}
                          </Typography>
                        </Box>
                      )}
                      {volunteer.skills && volunteer.skills.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {volunteer.skills.map((skill, idx) => (
                            <Chip key={idx} label={skill} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </Box>
                  }
                />

                <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                  <IconButton
                    color="success"
                    onClick={() => handleOpenDialog(volunteer, 'approve')}
                    title="Approve volunteer"
                  >
                    <CheckCircle />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleOpenDialog(volunteer, 'reject')}
                    title="Reject volunteer"
                  >
                    <Cancel />
                  </IconButton>
                </Box>
              </ListItem>
            </Box>
          ))}
        </List>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Volunteer' : 'Reject Volunteer'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {actionType === 'approve' ? (
              <>
                Are you sure you want to approve{' '}
                <strong>{selectedVolunteer?.displayName}</strong>? They will gain access to your
                organization's events and activities.
              </>
            ) : (
              <>
                Are you sure you want to reject{' '}
                <strong>{selectedVolunteer?.displayName}</strong>? They will not be able to
                access your organization.
              </>
            )}
          </DialogContentText>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Processing...' : actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PendingVolunteers;
